import {
  type ApiClient,
  type ApiResult,
  type CommandIntent,
  createIntent,
  isApiError,
  UnknownResultError,
} from '@saay/api-client';
import {
  isShebaChecksumValid,
  normalizeShebaInput,
  type Rials,
  rateToPercentInput,
} from '@saay/formatting';
import { isApprovalPending } from '@saay/web-app';

import type {
  AdminMerchant,
  CapacityAdjustmentRequest,
  CommercialSettingVersionCreate,
  InstallmentPlanSetReplace,
  PayoutAccountRequest,
} from '../api.js';
import { toCommercialVersion } from './merchant-commercial.js';
import { type PlanIntervalBounds, plansSchema, toPlanSetReplace } from './merchant-dialogs.js';

/**
 * The optional configuration of the merchant create wizard («تنظیمات»): the SAAY rate, the one
 * plan with its count and interval (D-62, D-79), the capacity limit, and the payout account. Each
 * item is its own command (AC §18.4 M10, §23.3 I1, §18.4 M12, §18.5 P6).
 *
 * Every merchant-level command takes the merchant `expectedVersion` and bumps it (AC §18.4, §7.2),
 * and only the plan answer carries the new version. So the wizard reads the merchant again before
 * each versioned command: sending the version from before an earlier command answers
 * `409 RESOURCE_VERSION_CONFLICT` (the production failure: capacity, then the rate, 409, and the plan
 * and the payout account never sent).
 */

export type SetupKey = 'commercial' | 'plan' | 'capacity' | 'payout';

/** The order the wizard sends the items in. Each is independent of the others. */
export const SETUP_ORDER: readonly SetupKey[] = ['commercial', 'plan', 'capacity', 'payout'];

export const SETUP_LABELS: Readonly<Record<SetupKey, string>> = {
  capacity: 'سقف ظرفیت',
  commercial: 'نرخ SAAY',
  payout: 'حساب تسویه',
  plan: 'طرح اقساطی',
};

/** What the admin entered on the step; an empty value means «later, on the merchant page». */
export interface SetupValues {
  readonly capacityRials: Rials | null;
  /** The API decimal rate (`0.080000`), or `''`. */
  readonly feeRate: string;
  readonly installmentCount: string;
  readonly intervalDays: string;
  readonly planType: '' | 'END_OF_SALE_MONTH' | 'NUMBERED';
  readonly sheba: string;
  readonly walletCash: boolean;
}

export const EMPTY_SETUP: SetupValues = {
  capacityRials: null,
  feeRate: '',
  installmentCount: '',
  intervalDays: '30',
  planType: '',
  sheba: '',
  walletCash: false,
};

export const SETUP_REASON = 'تنظیمات اولیه هنگام ثبت فروشگاه';

export type SetupStatus = 'idle' | 'pending' | 'done' | 'approval' | 'error' | 'unknown';

export interface SetupItem {
  /** The server's 2xx answer of a saved item (the toast's proof, never optimistic). */
  readonly confirmedBy?: ApiResult<unknown>;
  readonly error?: unknown;
  /**
   * The body last sent. After an unknown or a retryable outcome it is sent again unchanged, so the
   * same idempotency key answers (AC §7.1) and nothing is recorded twice.
   */
  readonly sent?: unknown;
  readonly status: SetupStatus;
}

export type SetupProgress = Readonly<Record<SetupKey, SetupItem>>;

export const IDLE_SETUP: SetupProgress = {
  capacity: { status: 'idle' },
  commercial: { status: 'idle' },
  payout: { status: 'idle' },
  plan: { status: 'idle' },
};

/** Whether the item was filled in on the step. */
export function isSetupFilled(key: SetupKey, values: SetupValues): boolean {
  switch (key) {
    case 'capacity':
      return values.capacityRials !== null && values.capacityRials > 0;
    case 'commercial':
      return values.feeRate !== '';
    case 'payout':
      return values.sheba.trim() !== '';
    case 'plan':
      return values.planType !== '' || values.walletCash;
    default:
      return false;
  }
}

/** The items to send: filled in and allowed for the caller, in {@link SETUP_ORDER}. */
export function wantedSetup(values: SetupValues, allowed: ReadonlySet<SetupKey>): SetupKey[] {
  return SETUP_ORDER.filter((key) => allowed.has(key) && isSetupFilled(key, values));
}

/** The item was saved (or, for a non-manager, submitted for approval). */
export function isSetupSettled(item: SetupItem): boolean {
  return item.status === 'done' || item.status === 'approval';
}

/** Resend the same body with the same key: the last outcome may have been recorded. */
function isSticky(item: SetupItem): boolean {
  if (item.sent === undefined) return false;
  if (item.status === 'unknown') return true;
  return item.status === 'error' && isApiError(item.error) && item.error.retryable;
}

/** The item's fields can no longer change: it is sending, saved, or its outcome is open. */
export function isSetupLocked(item: SetupItem): boolean {
  return item.status === 'pending' || isSetupSettled(item) || isSticky(item);
}

/** The Persian line of a failed item (`messageFa` of a business error, else a neutral line). */
export function setupErrorMessage(item: SetupItem): string | undefined {
  if (item.status === 'unknown') {
    return 'نتیجه مشخص نیست؛ «بررسی وضعیت و ادامه» همان درخواست را دوباره می‌فرستد.';
  }
  if (item.status !== 'error') return undefined;
  const error = item.error;
  if (isApiError(error)) return error.messageFa;
  if (error !== null && typeof error === 'object' && 'messageFa' in error) {
    const message = (error as { messageFa: unknown }).messageFa;
    if (typeof message === 'string') return message;
  }
  return 'ثبت انجام نشد؛ دوباره تلاش کنید.';
}

export interface SetupNotSaved {
  readonly key: SetupKey;
  readonly label: string;
  readonly messageFa: string;
}

/** Router state of the merchant page when the wizard left configuration items unsaved. */
export interface MerchantCreatedState {
  readonly setupNotSaved: readonly SetupNotSaved[];
}

function isSetupKey(value: unknown): value is SetupKey {
  return typeof value === 'string' && (SETUP_ORDER as readonly string[]).includes(value);
}

/** Whether the merchant now has the item (set later on the merchant page). */
function hasSetupItem(key: SetupKey, merchant: AdminMerchant): boolean {
  switch (key) {
    case 'commercial':
      return merchant.commercialSetting !== null;
    case 'plan':
      return merchant.activePlanSet.length > 0;
    case 'capacity':
      return merchant.capacity !== null && merchant.capacity.approvedLimitIrr > 0;
    case 'payout':
      return merchant.payoutAccount !== undefined && merchant.payoutAccount !== null;
    default:
      return false;
  }
}

/**
 * The items from `location.state` that the merchant still lacks; anything else is ignored. An item
 * set afterwards on the merchant page drops off the list.
 */
export function readMerchantCreatedState(
  state: unknown,
  merchant?: AdminMerchant,
): MerchantCreatedState | undefined {
  if (state === null || typeof state !== 'object' || !('setupNotSaved' in state)) {
    return undefined;
  }
  const list = (state as { setupNotSaved: unknown }).setupNotSaved;
  if (!Array.isArray(list)) return undefined;
  const items = list.filter(
    (item): item is SetupNotSaved =>
      item !== null &&
      typeof item === 'object' &&
      isSetupKey((item as { key?: unknown }).key) &&
      typeof (item as { label?: unknown }).label === 'string' &&
      typeof (item as { messageFa?: unknown }).messageFa === 'string' &&
      (merchant === undefined || !hasSetupItem((item as SetupNotSaved).key, merchant)),
  );
  return items.length === 0 ? undefined : { setupNotSaved: items };
}

export interface SetupFieldErrors {
  readonly installmentCount?: string;
  readonly intervalDays?: string;
  readonly planType?: string;
  readonly sheba?: string;
}

/**
 * Checks the step before anything is sent (the server decides again): a NUMBERED plan needs its
 * count and an interval within the platform bounds when they are known (AC §23.3), and a Sheba its
 * checksum (AC §18.5). Only allowed, filled items are checked.
 */
export function validateSetup(
  values: SetupValues,
  allowed: ReadonlySet<SetupKey>,
  bounds: PlanIntervalBounds | undefined,
  today: string,
): SetupFieldErrors {
  const errors: { -readonly [K in keyof SetupFieldErrors]?: string } = {};
  if (allowed.has('plan') && (values.planType !== '' || values.walletCash)) {
    const parsed = plansSchema(bounds).safeParse({
      effectiveFrom: today,
      installmentCount: values.installmentCount,
      intervalDays: values.intervalDays,
      reason: SETUP_REASON,
      type: values.planType,
      walletCash: values.walletCash,
    });
    if (!parsed.success) {
      for (const issue of parsed.error.issues) {
        const path = String(issue.path[0] ?? '');
        if (path === 'installmentCount' && errors.installmentCount === undefined) {
          errors.installmentCount = issue.message;
        } else if (path === 'intervalDays' && errors.intervalDays === undefined) {
          errors.intervalDays = issue.message;
        } else if (path === 'type' && errors.planType === undefined) {
          errors.planType = issue.message;
        }
      }
    }
  }
  if (
    allowed.has('payout') &&
    values.sheba.trim() !== '' &&
    !isShebaChecksumValid(normalizeShebaInput(values.sheba))
  ) {
    errors.sheba = 'شبا را به شکل IR و ۲۴ رقم وارد کنید؛ رقم کنترل آن درست نیست.';
  }
  return errors;
}

export function hasSetupErrors(errors: SetupFieldErrors): boolean {
  return Object.values(errors).some((message) => message !== undefined);
}

interface BodyContext {
  readonly holderName: string;
  readonly merchantId: string;
  readonly today: string;
  readonly values: SetupValues;
  readonly version: number;
}

/** The request body of an item (the version is the merchant's current one). */
export function setupBody(key: SetupKey, context: BodyContext): unknown {
  const { holderName, merchantId, today, values, version } = context;
  switch (key) {
    case 'commercial':
      return toCommercialVersion(
        {
          cashRate: '',
          effectiveFrom: today,
          rate: rateToPercentInput(values.feeRate),
          reason: SETUP_REASON,
        },
        version,
      ) satisfies CommercialSettingVersionCreate;
    case 'plan':
      return toPlanSetReplace(
        {
          effectiveFrom: today,
          installmentCount: values.installmentCount,
          intervalDays: values.intervalDays,
          reason: SETUP_REASON,
          type: values.planType,
          walletCash: values.walletCash,
        },
        version,
      ) satisfies InstallmentPlanSetReplace;
    case 'capacity':
      return {
        expectedVersion: version,
        newApprovedLimitIrr: values.capacityRials ?? 0,
        reason: SETUP_REASON,
      } satisfies CapacityAdjustmentRequest;
    case 'payout':
      return {
        holderName,
        ownerId: merchantId,
        ownerType: 'MERCHANT',
        reason: SETUP_REASON,
        sheba: normalizeShebaInput(values.sheba),
      } satisfies PayoutAccountRequest;
    default:
      return undefined;
  }
}

/** The payout request carries no merchant version (AC §18.5 P6). */
const VERSIONED: ReadonlySet<SetupKey> = new Set(['commercial', 'plan', 'capacity']);

function setupPath(key: SetupKey, merchantId: string): string {
  const merchant = `/admin/merchants/${encodeURIComponent(merchantId)}`;
  switch (key) {
    case 'commercial':
      return `${merchant}/commercial-setting-versions`;
    case 'plan':
      return `${merchant}/installment-plan-activations`;
    case 'capacity':
      return `${merchant}/capacity-adjustments`;
    default:
      return '/admin/payout-accounts';
  }
}

export interface RunSetupInput {
  readonly client: ApiClient;
  readonly holderName: string;
  /** One intent per item, kept across attempts (one key per intent, AC §7.1). */
  readonly intents: Partial<Record<SetupKey, CommandIntent>>;
  readonly merchantId: string;
  readonly onItem: (key: SetupKey, item: SetupItem) => void;
  readonly progress: SetupProgress;
  readonly today: string;
  readonly values: SetupValues;
  readonly wanted: readonly SetupKey[];
}

/**
 * Sends the wanted items that are not saved yet, in order, and returns the final progress. Before
 * each versioned command it reads the merchant's current version. A definitive refusal marks that
 * item and the next items still go; an unknown or retryable outcome stops the run, so «بررسی وضعیت و
 * ادامه» resends that same body first. Nothing is ever swallowed: every item ends `done`,
 * `approval`, `error`, or `unknown`.
 */
export async function runSetup(input: RunSetupInput): Promise<SetupProgress> {
  const { client, holderName, intents, merchantId, onItem, today, values, wanted } = input;
  const progress: Record<SetupKey, SetupItem> = { ...input.progress };
  const set = (key: SetupKey, item: SetupItem) => {
    progress[key] = item;
    onItem(key, item);
  };

  for (const key of wanted) {
    const current = progress[key];
    if (isSetupSettled(current)) continue;
    let intent = intents[key];
    if (intent === undefined) {
      intent = createIntent(`merchant.setup.${key}`);
      intents[key] = intent;
    }
    let body: unknown;
    if (isSticky(current)) {
      body = current.sent;
    } else {
      set(key, { status: 'pending' });
      try {
        const version = VERSIONED.has(key)
          ? (await client.get<AdminMerchant>(`/admin/merchants/${encodeURIComponent(merchantId)}`))
              .data.version
          : 0;
        body = setupBody(key, { holderName, merchantId, today, values, version });
      } catch (error) {
        // The merchant could not be read: nothing was sent for this item; stop and let the
        // admin try again.
        set(key, { error, status: 'error' });
        return progress;
      }
    }
    set(key, { sent: body, status: 'pending' });
    try {
      const result = await client.post<unknown>(setupPath(key, merchantId), { body, intent });
      intent.complete();
      set(key, {
        confirmedBy: result,
        sent: body,
        status: isApprovalPending(result) ? 'approval' : 'done',
      });
    } catch (error) {
      if (error instanceof UnknownResultError) {
        set(key, { error, sent: body, status: 'unknown' });
        return progress;
      }
      if (isApiError(error) && error.retryable) {
        set(key, { error, sent: body, status: 'error' });
        return progress;
      }
      intent.complete();
      set(key, { error, status: 'error' });
      if (!isApiError(error)) {
        // Not an answer of the API (for example no connection before sending): stop here.
        return progress;
      }
    }
  }
  return progress;
}
